<template>
  <div
    ref="container"
    class="relative lg:min-h-[200vh]">
    <div class="absolute h-full w-full">
      <div class="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
        <div class="absolute left-0 right-0 top-1/2 flex h-full w-full -translate-y-1/2 items-center justify-center">
          <div
            class="h-[100vw] w-[100vw] rounded-full blur-3xl duration-150 md:h-[480px] md:w-[480px] md:blur-2xl"
            :class="[`bg-${color}`]"
            :style="{
              transform: `scale3d(${scale}, ${scale}, 1)`
            }" />
        </div>
      </div>
    </div>

    <div
      class="sticky top-0 flex flex-col pb-16 lg:pb-24"
      :style="{
        paddingTop: `calc(50vh - ${textHeight / 2}px)`
      }">
      <div class="relative top-1/2 mb-20 flex flex-col items-center lg:mb-40">
        <p
          ref="textContainer"
          class="mx-auto px-6 text-center text-3xl text-white md:max-w-2xl lg:max-w-4xl lg:px-24 lg:text-5xl lg:leading-tight">
          {{ copy }}
        </p>
        <div
          v-if="outfit7neo"
          class="mt-14 flex flex-wrap items-center justify-center gap-4 text-sm md:text-base">
          <span class="w-full text-center text-white md:w-auto md:text-left">Published by:</span>
          <SvgOutfit7neoLogo class="h-9" />
        </div>
      </div>

      <Container class="relative w-full">
        <div
          class="mb-6 grid gap-6 transition duration-500 lg:mb-8"
          :class="[
            cardsVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
            cardPlacement === 'vertical' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
          ]">
          <CardBase
            v-for="(card, i) in cards"
            :key="i"
            :title="card.title"
            :copy="card.copy"
            :cta="card.cta"
            :logo="card.logo"
            :background-image="card.img"
            :background-image-mobile="card.imgMobile"
            :type="cardPlacement === 'vertical' ? 'product' : 'product-small'"
            :class="{ 'xs:h-[360px]': cardPlacement === 'horizontal' }"
            @click="handleClick(card.cta)" />
        </div>

        <div
          v-if="cta"
          class="flex justify-center py-6 lg:py-8">
          <ButtonBase
            :type="cta.type"
            :href="cta.href"
            :label="cta.title" />
        </div>
      </Container>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUpdated, ref, useTemplateRef } from 'vue'
  import { useRoute } from 'vue-router'
  import { useNuxtApp } from '#imports'

  import { scroll } from 'motion'

  import type { CtaType } from '../../models/button'

  type GradientCallOutProps = {
    copy: string
    cards: {
      title: string
      copy: string
      logo?: string
      cta: CtaType
      img: string
      imgMobile?: string
    }[]
    cta: CtaType
    color?: string
    outfit7neo?: boolean
    cardPlacement?: 'vertical' | 'horizontal'
  }

  withDefaults(defineProps<GradientCallOutProps>(), {
    cardPlacement: 'horizontal',
    outfit7neo: false,
    color: 'purple-aqua'
  })

  const { $event } = useNuxtApp()
  const route = useRoute()

  const textHeight = ref(0)
  const cardsVisible = ref(false)
  const scale = ref(1)
  const textContainer = useTemplateRef<HTMLParagraphElement>('textContainer')
  const container = useTemplateRef<HTMLDivElement>('container')
  const scrollEvent = ref<ReturnType<typeof scroll>>()

  function updateTextHeight() {
    //  text height is needed for sticky text to center
    //  without transform so we can control the
    //  spacing between text and cards
    if (textContainer.value.offsetHeight !== textHeight.value) {
      textHeight.value = textContainer.value.offsetHeight
    }
  }

  function changeBgSize(progress: number): void {
    if (!window) {
      return
    }

    // treshold on which percentage of container scrolled circle fills the screen
    // textHeight + padding
    if (container.value === null) {
      return
    }

    const fillTreshold =
      (textHeight.value + (window.innerHeight / 2 - textHeight.value / 2)) / container.value.offsetHeight

    //  calculate progress percentage based on when circle fills the screen
    const percentage = (1 / fillTreshold) * progress

    // We go over the edge to hide the blur and border radius for a simple animation
    const multiplier = 1.6
    const largerDimension = Math.max(window.innerWidth, window.innerHeight)

    // Max scale is calculated based on the width of the screen
    // and the size of the circle
    const maxScale = (largerDimension / 480) * multiplier

    if (progress > fillTreshold) {
      cardsVisible.value = true
    } else {
      cardsVisible.value = false
    }

    // Minimum value is 1, max is maxScale,
    // we increase it by a percentage of the difference (maxScale - 1)
    scale.value = percentage >= 1 ? maxScale : Math.max(1, Math.min(maxScale, 1 + (maxScale - 1) * percentage))
  }

  function handleClick(cta: CtaType) {
    if (!cta || !cta.href) {
      return
    }
    if (route.path.includes('our-work')) {
      return $event('Link', { props: { 'Our work page': cta.href } })
    }
  }

  onUpdated(() => {
    updateTextHeight()
  })

  onMounted(() => {
    scrollEvent.value = scroll(
      ({ y }) => {
        changeBgSize(y.progress)
      },
      {
        target: container.value,
        offset: ['start start', 'end end']
      }
    )

    updateTextHeight()
  })
</script>
